import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { adminApi, fileUrl } from "../../lib/api";
import { useSettings } from "../../lib/settings";
import AdminLayout from "../../components/app/AdminLayout";
import { ModalShell } from "../../components/app/AssignModals";
import { AddCastModal } from "../../components/app/AddCastModal";
import { StatusBadge } from "../../components/app/StatusBadge";
import { Loader } from "./Dashboard";
import { Search, Eye, Download, Drama, Send, Loader2, AlertTriangle, Check, UserPlus } from "lucide-react";

const CAST_STAGE = {
  preliminary: { label: "Preliminary", color: "#F59E0B" },
  invited: { label: "Invited", color: "#3B82F6" },
  confirmed: { label: "Confirmed", color: "#22C55E" },
};
const CastStageBadge = ({ stage }) => {
  const m = CAST_STAGE[stage];
  if (!m) return <span className="text-[#4a4843] text-xs">—</span>;
  return <span className="inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] uppercase tracking-wider border" style={{ color: m.color, borderColor: `${m.color}66` }}><span className="w-1.5 h-1.5 rounded-full" style={{ background: m.color }} />{m.label}</span>;
};

const FilterSelect = ({ value, onChange, options, ...rest }) => (
  <select {...rest} value={value} onChange={(e) => onChange(e.target.value)} className="text-sm bg-[#0A0A0A] border border-[#262626] px-3 py-2 focus:outline-none focus:border-blood">
    {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);

export default function Cast() {
  const { formatDate } = useSettings();
  const [data, setData] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [filters, setFilters] = useState({ status: "all", gender: "all", search: "", sort: "full_name" });
  const [offerFor, setOfferFor] = useState(null);
  const [assignChecked, setAssignChecked] = useState(false);
  const [personalMsg, setPersonalMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const navigate = useNavigate();

  const openOffer = (a) => { setOfferFor(a); setAssignChecked(false); setPersonalMsg(""); };
  const sendOffer = async () => {
    setSending(true);
    try {
      const { data: r } = await adminApi.post(`/admin/applicants/${offerFor.id}/send-offer`, { personal_message: personalMsg });
      toast.success(`Role offer sent to ${offerFor.full_name}${r?.role_name ? ` for ${r.role_name}` : ""}`);
      setOfferFor(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send offer.");
    } finally { setSending(false); }
  };
  const clearReview = async (a) => {
    try {
      await adminApi.post(`/admin/applicants/${a.id}/clear-availability-review`);
      toast.success(`Availability review cleared for ${a.full_name}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to clear.");
    }
  };

  const load = () => {
    const params = new URLSearchParams({ assigned_only: "true", limit: "500" });
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") params.set(k, v); });
    adminApi.get(`/admin/applicants?${params.toString()}`).then(({ data }) => setData(data));
  };

  useEffect(() => { load(); }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { adminApi.get("/admin/statuses").then(({ data }) => setStatuses(data.items)); }, []);

  const exportCsv = () => {
    if (!data) return;
    const rows = [["Name", "Email", "Phone", "Assigned Role(s)", "Status"]];
    data.items.forEach((a) => rows.push([a.full_name, a.email, a.phone, (a.assigned_roles || []).join(" | "), a.status]));
    const csv = rows.map((r) => r.map((c) => `"${String(c || "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const el = document.createElement("a");
    el.href = URL.createObjectURL(blob);
    el.download = `cast-${new Date().toISOString().slice(0, 10)}.csv`;
    el.click();
  };

  return (
    <AdminLayout
      title="Cast"
      subtitle="Everyone in the casting pool — preliminary, invited and confirmed"
      actions={<div className="flex items-center gap-2">
        <button onClick={() => setAddOpen(true)} data-testid="cast-add-member-btn" className="flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider bg-blood text-white hover:bg-[#FF3333]">
          <UserPlus size={14} /> Add Cast Member
        </button>
        <button onClick={exportCsv} data-testid="cast-export-btn" className="flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider border border-[#262626] text-[#A19D94] hover:border-blood hover:text-blood">
          <Download size={14} /> Export
        </button>
      </div>}
    >
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6862]" />
          <input
            data-testid="cast-search-input"
            value={filters.search} onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
            placeholder="Search cast…"
            className="w-full pl-9 pr-3 py-2 text-sm bg-[#0A0A0A] border border-[#262626] focus:outline-none focus:border-blood"
          />
        </div>
        <FilterSelect data-testid="cast-filter-status" value={filters.status} onChange={(v) => setFilters((f) => ({ ...f, status: v }))} options={[["all", "All Status"], ...statuses.map((s) => [s.key, s.label])]} />
        <FilterSelect data-testid="cast-filter-gender" value={filters.gender} onChange={(v) => setFilters((f) => ({ ...f, gender: v }))} options={[["all", "All Gender"], ["Male", "Male"], ["Female", "Female"], ["Non-binary", "Non-binary"], ["Other", "Other"]]} />
        <FilterSelect data-testid="cast-filter-sort" value={filters.sort} onChange={(v) => setFilters((f) => ({ ...f, sort: v }))} options={[["full_name", "Name A–Z"], ["-full_name", "Name Z–A"], ["-submitted_at", "Newest first"], ["status", "Status"]]} />
      </div>

      {!data ? <Loader /> : (
        <div className="border border-[#1a1a1a] bg-[#0A0A0A] overflow-x-auto">
          <table className="w-full text-sm" data-testid="cast-table">
            <thead>
              <tr className="border-b border-[#1a1a1a] text-left">
                <th className="px-3 py-3 label-mini">Name</th>
                <th className="px-3 py-2 label-mini">Assigned Role</th>
                <th className="px-3 py-2 label-mini">Casting</th>
                <th className="px-3 py-2 label-mini">Gender</th>
                <th className="px-3 py-2 label-mini">Date Submitted</th>
                <th className="px-3 py-2 label-mini">Status</th>
                <th className="px-3 py-2 label-mini">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.items.length === 0 ? (
                <tr><td colSpan={7} className="py-12 text-center text-[#6B6862]">No cast members yet — add actors to the cast from their profile, or assign roles via Role Match / the Roster.</td></tr>
              ) : data.items.map((a) => (
                <tr
                  key={a.id}
                  onClick={() => navigate(`/admin/applicants/${a.id}`)}
                  data-testid={`cast-row-${a.id}`}
                  className="border-b border-[#1a1a1a] hover:bg-[#101010] transition-colors cursor-pointer"
                >
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-3">
                      {a.headshot_file_id ? (
                        <img src={fileUrl(a.headshot_file_id, { thumb: true })} alt="" loading="lazy" decoding="async" className="w-9 h-9 object-cover border border-[#262626] bg-[#1a1a1a]" />
                      ) : (
                        <div className="w-9 h-9 bg-[#1a1a1a] border border-[#262626] flex items-center justify-center font-display text-blood text-sm">{a.full_name?.[0]}</div>
                      )}
                      <span className="text-[#F2EBE1]">{a.full_name}</span>
                    </div>
                  </td>
                  <td className="px-3 py-3" data-testid={`cast-roles-${a.id}`}>
                    <div className="flex flex-wrap gap-1.5">
                      {(a.assigned_roles || []).map((r) => (
                        <span key={r} className="inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] uppercase tracking-wider border border-blood/40 bg-blood/5 text-[#F2EBE1]">
                          <Drama size={11} className="text-blood" /> {r}
                        </span>
                      ))}
                      {(a.assigned_roles || []).length === 0 && (
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] uppercase tracking-wider border border-[#3a3835] text-[#8a857d]" data-testid={`cast-norole-${a.id}`}>
                          In cast · no role yet
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-3 align-top" data-testid={`cast-stage-${a.id}`} onClick={(e) => a.availability_review_needed && e.stopPropagation()}>
                    <CastStageBadge stage={a.cast_stage} />
                    {a.availability_review_needed && (
                      <div className="mt-2 max-w-[220px]" data-testid={`cast-availchanged-${a.id}`}>
                        <div className="inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] uppercase tracking-wider border border-[#F59E0B]/50 bg-[#F59E0B]/10 text-[#F59E0B]">
                          <AlertTriangle size={11} /> Availability changed
                        </div>
                        {a.availability_change_note && (
                          <p className="text-[11px] text-[#A19D94] italic mt-1 leading-snug">"{a.availability_change_note}"</p>
                        )}
                        <button onClick={() => clearReview(a)} data-testid={`cast-availchanged-clear-${a.id}`}
                          className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#6B6862] hover:text-[#F59E0B]">
                          <Check size={11} /> Mark handled
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-3 text-[#A19D94]">{a.gender || "—"}</td>
                  <td className="px-3 py-3 font-mono text-xs text-[#A19D94]">{formatDate(a.submitted_at)}</td>
                  <td className="px-3 py-3"><StatusBadge status={a.status} statuses={statuses} /></td>
                  <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-3">
                      {a.status !== "contracted" && (a.assigned_roles || []).length > 0 && (
                        <button onClick={() => openOffer(a)} data-testid={`cast-send-offer-${a.id}`}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-wider bg-blood text-white hover:bg-[#FF3333]">
                          <Send size={12} /> {a.cast_stage === "invited" ? "Resend Offer" : "Send Offer"}
                        </button>
                      )}
                      <Link to={`/admin/applicants/${a.id}`} data-testid={`cast-view-${a.id}`} className="text-[#A19D94] hover:text-blood inline-flex"><Eye size={16} /></Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex items-center justify-between px-4 py-3 border-t border-[#1a1a1a] text-xs text-[#A19D94] font-mono">
            <span>{data.items.length} cast member{data.items.length === 1 ? "" : "s"}</span>
          </div>
        </div>
      )}

      {addOpen && <AddCastModal onClose={() => setAddOpen(false)} onAdded={() => { setAddOpen(false); load(); }} />}

      {offerFor && (
        <ModalShell
          testid="cast-offer-modal"
          title={offerFor.cast_stage === "invited" ? "Resend Role Offer" : "Send Role Offer"}
          subtitle={`${offerFor.full_name} · ${(offerFor.assigned_roles || []).join(", ")}`}
          onClose={() => setOfferFor(null)}
        >
          <label className="flex items-start gap-2.5 cursor-pointer mb-4" data-testid="cast-offer-confirm">
            <input type="checkbox" checked={assignChecked} onChange={(e) => setAssignChecked(e.target.checked)} className="accent-blood mt-0.5" />
            <span className="text-sm text-[#F2EBE1]">
              {offerFor.cast_stage === "invited" ? (
                <>Re-send the role-offer email for <span className="text-blood font-medium">{(offerFor.assigned_roles || []).join(", ")}</span> so they can accept and sign their contract.</>
              ) : (
                <>Assign <span className="text-blood font-medium">{(offerFor.assigned_roles || []).join(", ")}</span> for real and send the role-offer email so they can accept and sign their contract.</>
              )}
            </span>
          </label>
          <label className="block text-xs uppercase tracking-wider text-[#8a857d] mb-1.5">Personal message (optional)</label>
          <textarea
            value={personalMsg}
            onChange={(e) => setPersonalMsg(e.target.value)}
            rows={4}
            data-testid="cast-offer-message"
            placeholder="Add a personal note to this actor…"
            className="w-full px-3 py-2 text-sm bg-[#0A0A0A] border border-[#262626] focus:outline-none focus:border-blood resize-none"
          />
          <p className="text-[11px] text-[#6B6862] mt-1.5">Inserted into the role-offer email wherever you place <span className="font-mono text-[#8a857d]">{"{{personal_message}}"}</span> in the template.</p>
          <div className="flex items-center justify-end gap-2 mt-5">
            <button onClick={() => setOfferFor(null)} data-testid="cast-offer-cancel" className="px-4 py-2 text-xs uppercase tracking-wider border border-[#262626] text-[#A19D94] hover:text-[#F2EBE1]">Cancel</button>
            <button onClick={sendOffer} disabled={!assignChecked || sending} data-testid="cast-offer-send"
              className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider bg-blood text-white hover:bg-[#FF3333] disabled:opacity-40 disabled:cursor-not-allowed">
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Assign &amp; Send
            </button>
          </div>
        </ModalShell>
      )}
    </AdminLayout>
  );
}
